import React, { useState, useEffect } from 'react';
import ApiService from '../services/api';

const PersonalExperience = ({ 
  userExperiences, 
  setUserExperiences, 
  experiencesLoading, 
  setExperiencesLoading,
  getCachedExperiences,
  onEditExperience,
  onRemoveExperience,
  onShowAllExperiences,
  onAddExperience
}) => {

  // Load cached data first for instant display
  useEffect(() => {
    const cachedExperiences = getCachedExperiences();
    if (cachedExperiences.length > 0) {
      setUserExperiences(cachedExperiences);
    }
    // Then fetch fresh data in background
    fetchUserExperiences();
  }, [getCachedExperiences]);

  const fetchUserExperiences = async () => {
    setExperiencesLoading(true);
    try {
      const response = await ApiService.get('/experiences');
      if (response.success) {
        setUserExperiences(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching user experiences:', error);
    } finally {
      setExperiencesLoading(false);
    }
  };


  // Format date range with styled Present badge
  const formatDateRangeWithBadge = (startDate, endDate, isCurrent) => {
    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short' 
      });
    };

    const start = formatDate(startDate);
    
    if (isCurrent) {
      return (
        <>
          {start} - <span className="present-badge">Present</span>
        </>
      );
    } else {
      const end = formatDate(endDate);
      return `${start} - ${end}`;
    }
  };

  return (
    <div className="personal-experience-section">
      <div className="section-header">
        <h3>Experience</h3>
        <button 
          className="header-btn add-skills-btn"
          onClick={onAddExperience}
          title="Add Experience"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          <span>Add Experience</span>
        </button>
      </div>

      <div className="experience-container">
        {experiencesLoading ? (
          <div className="experience-loading">
            <div className="spinner-small"></div>
            <span>Loading experiences...</span>
          </div>
        ) : userExperiences && userExperiences.length > 0 ? (
          <div className="experience-list">
            {userExperiences.slice(0, 3).map((experience, index) => (
              <div key={experience.id} className={`experience-card ${experience.is_current ? 'current-job' : ''}`}>
                <div className="experience-header">
                  <div className="experience-title-section">
                    {experience.company_logo && (
                      <img 
                        src={experience.company_logo} 
                        alt={`${experience.company} logo`}
                        className="experience-company-logo"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    <div className="experience-title-content">
                      <h4 className="experience-title">{experience.title}</h4>
                      <p className="experience-company">{experience.company}</p>
                    </div>
                  </div>
                  <div className="experience-actions">
                    <button 
                      className="edit-experience-btn"
                      onClick={() => onEditExperience(experience)}
                      title="Edit experience"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                      </svg>
                    </button>
                    <button 
                      className="remove-experience-btn"
                      onClick={() => onRemoveExperience(experience.id)}
                      title="Remove experience"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3,6 5,6 21,6"></polyline>
                        <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="experience-details">
                  <div className="experience-meta">
                    <div className="experience-duration">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12,6 12,12 16,14"></polyline>
                      </svg>
                      <span>
                        {experience.date_range || formatDateRangeWithBadge(experience.start_date, experience.end_date, experience.is_current)}
                      </span>
                    </div>
                    {experience.location && (
                      <div className="experience-location">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                          <circle cx="12" cy="10" r="3"></circle>
                        </svg>
                        <span>{experience.location}</span>
                      </div>
                    )}
                    <div className="experience-type">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                        <line x1="8" y1="21" x2="16" y2="21"></line>
                        <line x1="12" y1="17" x2="12" y2="21"></line>
                      </svg>
                      <span>{experience.formatted_employment_type || experience.employment_type}</span>
                    </div>
                  </div>
                  
                  {experience.description && (
                    <div className="experience-description">
                      <p>{experience.description}</p>
                    </div>
                  )}
                  
                  {experience.skills_used && experience.skills_used.length > 0 && (
                    <div className="experience-skills">
                      <h5>Skills Used:</h5>
                      <div className="skills-tags">
                        {experience.skills_used.map((skill, skillIndex) => (
                          <span key={skillIndex} className="skill-tag-small">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {userExperiences.length > 3 && (
              <div className="view-all-experiences">
                <button 
                  className="view-all-btn"
                  onClick={async () => {
                    await fetchUserExperiences();
                    onShowAllExperiences();
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 3h18v18H3zM9 9h6v6H9z"></path>
                    <path d="M9 3v6M15 3v6M9 15v6M15 15v6"></path>
                  </svg>
                  View All {userExperiences.length} Experiences
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="no-experience">
            <div className="no-experience-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                <path d="M6 21v-2a4 4 0 0 1 4-4h.5"></path>
              </svg>
            </div>
            <div className="no-experience-content">
              <h4>Build Your Professional Story</h4>
              <p>Showcase your career journey and achievements to stand out to potential employers and clients</p>
              <div className="no-experience-features">
                <div className="feature-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22,4 12,14.01 9,11.01"></polyline>
                  </svg>
                  <span>Highlight your achievements</span>
                </div>
                <div className="feature-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22,4 12,14.01 9,11.01"></polyline>
                  </svg>
                  <span>Showcase your skills</span>
                </div>
                <div className="feature-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22,4 12,14.01 9,11.01"></polyline>
                  </svg>
                  <span>Build credibility</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default PersonalExperience;
